import React, { useState, useEffect, useRef, useCallback } from "react";
import { getStoredUser } from "../controllers/authController";
import { postCustomMachines } from "../controllers/machineController";
import api from "../utils/api";
import { SolarIcon, GridIcon, GeneratorIcon, LoadIcon, MotorIcon } from "../components/ColorfulIcons";

// Parameter mapping: index corresponds to bit position in custom_parameter
const PARAMETERS = [
  { id: "power", label: "Power", unit: "kW", key: "power_kw" },
  { id: "voltage", label: "Voltage", unit: "V", key: "voltage" },
  { id: "power_factor", label: "Power Factor", unit: "", key: "power_factor" },
  { id: "current", label: "Current", unit: "A", key: "current" },
  { id: "frequency", label: "Frequency", unit: "Hz", key: "frequency" },
  { id: "thd_voltage", label: "THD Voltage", unit: "%", key: "thd_voltage" },
  { id: "thd_current", label: "THD Current", unit: "%", key: "thd_current" },
  { id: "tap", label: "TAP", unit: "", key: "tap" },
];

const DEFAULT_MACHINE_CATEGORIES = ["GRID", "GENERATOR", "SOLAR"];
const DEFAULT_CATEGORY_PRIORITY = {
  GRID: 0,
  GENERATOR: 1,
  SOLAR: 2,
};

const CustomizationTab = () => {
  const storedUser = getStoredUser();
  const username = storedUser?.username || "";

  const [allMachines, setAllMachines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [initialConfig, setInitialConfig] = useState([]); // Track initial state
  const [hasChanges, setHasChanges] = useState(false); // Track if there are unsaved changes
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [modalMachineName, setModalMachineName] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const toastTimer = React.useRef(null);

  const showToast = (message, type = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ show: true, message, type });
    toastTimer.current = setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3500);
  };

  const normalizeCategory = (category) =>
    (category || "").toString().trim().toUpperCase();

  const buildDefaultMachineSelection = (machines) => {
    const defaultCategorySet = new Set(DEFAULT_MACHINE_CATEGORIES);

    return machines
      .filter((machine) => defaultCategorySet.has(normalizeCategory(machine.category)))
      .sort((a, b) => {
        const catA = DEFAULT_CATEGORY_PRIORITY[normalizeCategory(a.category)] ?? Number.MAX_SAFE_INTEGER;
        const catB = DEFAULT_CATEGORY_PRIORITY[normalizeCategory(b.category)] ?? Number.MAX_SAFE_INTEGER;
        if (catA !== catB) return catA - catB;
        return (a.machine_name || "").localeCompare(b.machine_name || "");
      })
      .map((machine, index) => ({
        meter_id: machine.meter_id,
        machine_name: machine.machine_name,
        category: machine.category,
        custom_parameter: "00000000",
        custom_order: index + 1,
        live_data: machine.live_data || {},
      }));
  };

  const applyDefaultCustomization = async (machines) => {
    const defaults = buildDefaultMachineSelection(machines);
    setSelectedMachines(defaults);

    if (!defaults.length) {
      setInitialConfig([]);
      return;
    }

    const payload = defaults.map((machine) => ({
      machine_name: machine.machine_name,
      custom_parameter: machine.custom_parameter,
      custom_order: machine.custom_order,
    }));

    try {
      await postCustomMachines(username, payload);
      setInitialConfig(JSON.parse(JSON.stringify(defaults)));
    } catch (error) {
      // Keep defaults visible, but allow manual save if auto-persist fails.
      console.error("Failed to auto-apply default customization:", error);
      setInitialConfig([]);
      showToast("Default cards loaded. Please click Save Changes to persist.", "error");
    }
  };

  // Fetch machines and custom configuration on mount
  useEffect(() => {
    if (username) {
      fetchData();
    }
  }, [username]);

  // Track changes by comparing current state with initial config
  useEffect(() => {
    if (initialConfig.length === 0 && selectedMachines.length === 0) {
      setHasChanges(false);
      return;
    }

    // Check if configurations are different
    const configChanged =
      JSON.stringify(selectedMachines) !== JSON.stringify(initialConfig);
    setHasChanges(configChanged);
  }, [selectedMachines, initialConfig]);

  // Warn user about unsaved changes when leaving page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all machines from the latest data API
      const latestResponse = await api.get(`/latest/${username}`);
      const machines = latestResponse.data?.readings || [];

      // Transform to our format
      const transformedMachines = machines.map((reading) => ({
        meter_id: reading.meter_id || reading.machine_name,
        machine_name: reading.machine_name,
        category: reading.category || reading.machine_type,
        live_data: {
          power_kw: reading.power_kw,
          voltage: reading.voltage,
          current: reading.current,
          power_factor: reading.power_factor,
          frequency: reading.frequency,
          thd_voltage: reading.thd_voltage,
          thd_current: reading.thd_current,
          tap: reading.tap,
        },
      }));

      setAllMachines(transformedMachines);

      // Try to fetch custom configuration
      try {
        const customResponse = await api.get(
          `/get_custom_machines?username=${username}`,
        );
        const customMachinesResponse = Array.isArray(customResponse.data?.machines)
          ? customResponse.data.machines
          : [];

        if (
          customResponse.data?.status === "success" &&
          customMachinesResponse.length > 0
        ) {
          // Merge custom config with machine data
          const customMachines = customMachinesResponse.map(
            (customMachine) => {
              const machineData = transformedMachines.find(
                (m) => m.machine_name === customMachine.machine_name,
              );
              return {
                ...customMachine,
                category: machineData?.category,
                live_data: {
                  ...(machineData?.live_data || {}),
                  ...(customMachine.live_data || {}),
                },
              };
            },
          );

          // Sort by custom_order
          const sortedMachines = customMachines.sort(
            (a, b) => a.custom_order - b.custom_order,
          );
          setSelectedMachines(sortedMachines);
          setInitialConfig(JSON.parse(JSON.stringify(sortedMachines))); // Deep copy for comparison
        } else {
          await applyDefaultCustomization(transformedMachines);
        }
      } catch (customError) {
        console.error("Failed to fetch custom configuration:", customError);
      }
    } catch (error) {
      console.error("Failed to fetch machines:", error);
      alert("Failed to load machines. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMachine = (machine) => {
    // Check if machine already exists
    if (selectedMachines.find((m) => m.machine_name === machine.machine_name)) {
      alert("Machine already added!");
      return;
    }

    // Add machine with default parameters (all zeros)
    const newMachine = {
      meter_id: machine.meter_id,
      machine_name: machine.machine_name,
      category: machine.category,
      custom_parameter: "00000000",
      custom_order: selectedMachines.length + 1,
      live_data: machine.live_data || {},
    };

    immediateSaveRef.current = true; // structural change → save immediately
    setSelectedMachines([...selectedMachines, newMachine]);
  };

  const handleRemoveMachine = (machineName) => {
    const filtered = selectedMachines.filter(
      (m) => m.machine_name !== machineName,
    );
    // Reorder
    const reordered = filtered.map((m, idx) => ({
      ...m,
      custom_order: idx + 1,
    }));
    immediateSaveRef.current = true; // structural change → save immediately
    setSelectedMachines(reordered);
  };

  const handleToggleParameter = (machineName, paramIndex) => {
    setSelectedMachines((prev) =>
      prev.map((machine) => {
        if (machine.machine_name === machineName) {
          const params = machine.custom_parameter.split("");
          params[paramIndex] = params[paramIndex] === "1" ? "0" : "1";
          return { ...machine, custom_parameter: params.join("") };
        }
        return machine;
      }),
    );
  };

  // Auto-save: persist current config without refetching (avoids card flicker).
  const autoSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = selectedMachines.map((m) => ({
        machine_name: m.machine_name,
        custom_parameter: m.custom_parameter,
        custom_order: m.custom_order,
      }));

      await postCustomMachines(username, payload);

      // Update baseline to current state so hasChanges resets to false
      setInitialConfig(JSON.parse(JSON.stringify(selectedMachines)));
      setHasChanges(false);
    } catch (error) {
      console.error("Auto-save failed:", error);
      showToast("Failed to save changes. Will retry on next change.", "error");
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMachines, username]);

  // Keep latest hasChanges / autoSave in refs for the unmount (save-on-leave) handler.
  const hasChangesRef = useRef(false);
  const autoSaveRef = useRef(autoSave);
  useEffect(() => {
    hasChangesRef.current = hasChanges;
  }, [hasChanges]);
  useEffect(() => {
    autoSaveRef.current = autoSave;
  }, [autoSave]);

  // Immediate save ONLY for structural changes (add / remove / reorder machine).
  // Parameter toggles do NOT set this flag — they save on leave instead.
  const immediateSaveRef = useRef(false);
  useEffect(() => {
    if (immediateSaveRef.current) {
      immediateSaveRef.current = false;
      autoSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMachines]);

  // Save-on-leave: when the user navigates away from the tab (unmount),
  // persist any unsaved changes (covers parameter toggles).
  useEffect(() => {
    return () => {
      if (hasChangesRef.current && autoSaveRef.current) {
        autoSaveRef.current();
      }
    };
  }, []);

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (dropIndex) => {
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const reordered = [...selectedMachines];
    const [draggedItem] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, draggedItem);

    // Update custom_order
    const updated = reordered.map((m, idx) => ({
      ...m,
      custom_order: idx + 1,
    }));
    immediateSaveRef.current = true; // structural change → save immediately
    setSelectedMachines(updated);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Get available machines (not yet selected)
  const availableMachines = allMachines.filter(
    (m) => !selectedMachines.find((sm) => sm.machine_name === m.machine_name),
  );

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
          <p>Loading machines...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <div style={compactSelectorStyle}>
          <span style={{ fontWeight: "600", color: "#0f172a" }}>Add Machine:</span>
          <select
            style={selectStyle}
            onChange={(e) => {
              const machine = allMachines.find(
                (m) => m.machine_name === e.target.value,
              );
              if (machine) {
                handleAddMachine(machine);
                e.target.value = "";
              }
            }}
            defaultValue=""
            onFocus={(e) => (e.target.style.borderColor = "#1BA08E")}
            onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
          >
            <option value="" disabled>
              Select machine...
            </option>
            {availableMachines.map((machine) => (
              <option key={machine.machine_name} value={machine.machine_name}>
                {machine.machine_name}
              </option>
            ))}
          </select>
        </div>
        {/* Auto-save status (changes persist automatically, no button) */}
        <span
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: saving ? "#f59e0b" : hasChanges ? "#f59e0b" : "#1BA08E",
          }}
        >
          {saving ? "💾 Saving..." : hasChanges ? "• Unsaved changes" : "✓ All changes saved"}
        </span>
      </header>

      <div style={contentWrapperStyle}>
        {/* Selected Machines */}
        {selectedMachines.length === 0 ? (
          <div style={emptyStateStyle}>
            <div style={emptyIconStyle}>📊</div>
            <h3 style={{ margin: "16px 0 8px 0", color: "#0f172a" }}>
              No machines selected
            </h3>
            <p style={{ margin: 0, color: "#64748b" }}>
              Select a machine from the dropdown above to get started
            </p>
          </div>
        ) : (
          <div style={machinesGridStyle}>
            {selectedMachines.map((machine, index) => (
              <MachineCard onClickCard={() => setModalMachineName(machine.machine_name)}
                key={machine.machine_name}
                machine={machine}
                index={index}
                isDragging={draggedIndex === index}
                onRemove={handleRemoveMachine}
                onToggleParameter={handleToggleParameter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              />
            ))}
          </div>
        )}
      </div>
      <ParameterModal
        machine={selectedMachines.find((m) => m.machine_name === modalMachineName)}
        onClose={() => {
          setModalMachineName(null);
          // Save parameter changes when the selection menu closes
          if (hasChanges) autoSave();
        }}
        onToggleParameter={handleToggleParameter}
      />

      {toast.show && (
        <div style={toastStyle(toast.type)}>
          {toast.type === "success" ? "✓ " : "✕ "}
          {toast.message}
        </div>
      )}
    </div>
  );
};

const getCardImageByCategory = (category) => {
  const normalized = (category || "").toUpperCase();

  if (normalized === "GENERATOR") {
    return "/images/dashboard/Generator.jpg";
  }

  if (normalized === "GRID") {
    return "/images/dashboard/Grid.jpg";
  }

  if (normalized === "SOLAR") {
    return "/images/dashboard/Solar.jpg";
  }

  if (normalized === "MACHINE") {
    return "/images/dashboard/Machine.jpg";
  }

  // VIRTUAL and unmatched categories use neutral visual treatment.
  return null;
};

const MachineCard = ({
  machine,
  index,
  isDragging,
  onRemove,
  onClickCard,
  onToggleParameter,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const parameters = machine.custom_parameter.split("");
  const activeParams = PARAMETERS.filter((_, idx) => parameters[idx] === "1");
  const cardImageUrl = getCardImageByCategory(machine.category);
  const isVirtualCategory = (machine.category || "").toUpperCase() === "VIRTUAL";

  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={onDragOver}
      onDrop={() => onDrop(index)}
      onDragEnd={onDragEnd}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClickCard}
      style={{
        ...liveDataStyle,
        cursor: isDragging ? "grabbing" : "pointer",
        opacity: isDragging ? 0.5 : 1,
        transform: isDragging
          ? "scale(0.95)"
          : isHovered
            ? "translateY(-4px)"
            : "translateY(0)",
        boxShadow: isDragging
          ? "0 8px 30px rgba(0, 0, 0, 0.15)"
          : isHovered
            ? "0 10px 28px rgba(15, 23, 42, 0.14)"
            : "0 4px 14px rgba(15, 23, 42, 0.08)",
        borderColor: isHovered ? "#1BA08E" : "#dbe4f0",
      }}
    >
      <div
        style={{
          ...cardImageSectionStyle,
          ...(cardImageUrl
            ? {
                backgroundImage: `linear-gradient(180deg, rgba(15, 23, 42, 0.12) 0%, rgba(15, 23, 42, 0.22) 100%), url('${cardImageUrl}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }
            : {
                background:
                  "linear-gradient(135deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%)",
              }),
        }}
      >
        {isVirtualCategory && (
          <div style={virtualIconContainerStyle}>
            <svg
              viewBox="0 0 64 64"
              aria-label="Virtual node"
              role="img"
              style={virtualIconStyle}
            >
              <circle cx="32" cy="32" r="10" fill="#0f766e" />
              <circle cx="16" cy="18" r="6" fill="#1BA08E" />
              <circle cx="48" cy="18" r="6" fill="#1BA08E" />
              <circle cx="16" cy="46" r="6" fill="#1BA08E" />
              <circle cx="48" cy="46" r="6" fill="#1BA08E" />
              <line x1="24" y1="24" x2="20" y2="20" stroke="#0f766e" strokeWidth="2.5" />
              <line x1="40" y1="24" x2="44" y2="20" stroke="#0f766e" strokeWidth="2.5" />
              <line x1="24" y1="40" x2="20" y2="44" stroke="#0f766e" strokeWidth="2.5" />
              <line x1="40" y1="40" x2="44" y2="44" stroke="#0f766e" strokeWidth="2.5" />
            </svg>
            <span style={virtualIconLabelStyle}>VIRTUAL</span>
          </div>
        )}
      </div>

      <div style={cardContentSectionStyle}>
      <div
        style={{
          ...liveDataHeaderStyle,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ ...dragHandleStyle, color: "#64748b", textShadow: "none" }}>
            ⋮⋮
          </div>
          <span
            style={{
              fontSize: "14px",
              fontWeight: "700",
              color: "#0f172a",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "120px",
            }}
          >
            {machine.machine_name}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove(machine.machine_name);
            }}
            style={{
              ...removeButtonStyle,
              color: "#64748b",
            }}
            title="Remove machine"
            onMouseEnter={(e) => {
              e.target.style.background = "#fee2e2";
              e.target.style.color = "#dc2626";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "transparent";
              e.target.style.color = "#64748b";
            }}
          >
            ✕
          </button>
        </div>
      </div>
      
      {machine.live_data && Object.keys(machine.live_data).length > 0 && activeParams.length > 0 ? (
        <div style={liveDataGridStyle}>
          {PARAMETERS.map((param, idx) => {
            if (parameters[idx] !== "1") return null;
            const value = machine.live_data[param.key];
            if (value == null || value === undefined) return null;
            return (
              <div key={param.key} style={liveDataItemStyle}>
                <span
                  style={{
                    ...liveDataLabelStyle,
                    color: "#64748b",
                    textShadow: "none",
                  }}
                >
                  {param.label}
                </span>
                <span
                  style={{
                    ...liveDataValueStyle,
                    color: "#0f172a",
                    textShadow: "none",
                  }}
                >
                  {typeof value === "number" ? value.toFixed(2) : value}
                  {param.unit ? ` ${param.unit}` : ""}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            padding: '16px 0',
            textAlign: 'center',
            color: '#475569',
            textShadow: 'none',
            fontSize: '13px',
          }}
        >
          {activeParams.length === 0 ? "Click to select parameters" : "No live data available"}
        </div>
      )}
      </div>
    </div>
  );
};

// Parameter Modal
const ParameterModal = ({ machine, onClose, onToggleParameter }) => {
  if (!machine) return null;
  const parameters = machine.custom_parameter.split("");
  const activeParams = PARAMETERS.filter((_, idx) => parameters[idx] === "1");

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
          <h3 style={{ margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.2em' }}>⚙️</span> {machine.machine_name} Parameters
          </h3>
          <button onClick={onClose} style={closeModalBtnStyle}>✕</button>
        </div>
        <div style={parametersSectionStyle}>
          <h4 style={parametersTitleStyle}>
            Display Parameters{" "}
            {activeParams.length > 0 && `(${activeParams.length} selected)`}
          </h4>
          <div style={parametersGridStyle}>
            {PARAMETERS.map((param, idx) => {
              const isActive = parameters[idx] === "1";
              return (
                <button
                  key={param.id}
                  onClick={() => onToggleParameter(machine.machine_name, idx)}
                  style={{
                    ...parameterChipStyle,
                    ...(isActive ? parameterChipActiveStyle : parameterChipInactiveStyle),
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.target.style.borderColor = "#94a3b8";
                      e.target.style.background = "#f1f5f9";
                    } else {
                      e.target.style.transform = "scale(1.05)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.target.style.borderColor = "#e2e8f0";
                      e.target.style.background = "#f8fafc";
                    } else {
                      e.target.style.transform = "scale(1)";
                    }
                  }}
                >
                  <span>{param.label}</span>
                  {isActive && <span style={checkmarkStyle}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  padding: "16px",
  maxWidth: "1600px",
  margin: "0 auto",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "16px",
  gap: "24px",
  flexWrap: "wrap",
  paddingBottom: "12px",
  borderBottom: "1px solid #e2e8f0",
};

const titleStyle = {
  margin: "0 0 8px 0",
  fontSize: "32px",
  fontWeight: "700",
  background: "linear-gradient(135deg, #1BA08E 0%, #17a98b 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

const subtitleStyle = {
  margin: 0,
  color: "#64748b",
  fontSize: "16px",
};

const contentWrapperStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "24px",
};

const selectorCardStyle = {
  background: "white",
  padding: "24px",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  border: "2px solid transparent",
  transition: "all 0.3s ease",
};

const sectionTitleStyle = {
  margin: "0 0 16px 0",
  fontSize: "18px",
  fontWeight: "600",
  color: "#0f172a",
  display: "flex",
  alignItems: "center",
};

const selectStyle = {
  minWidth: "200px",
  padding: "8px 12px",
  borderRadius: "10px",
  border: "2px solid #e2e8f0",
  fontSize: "16px",
  cursor: "pointer",
  transition: "all 0.2s",
  outline: "none",
  background: "white",
};



const loadingStyle = {
  textAlign: "center",
  padding: "64px 24px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
};

const spinnerStyle = {
  width: "48px",
  height: "48px",
  border: "4px solid #e2e8f0",
  borderTop: "4px solid #1BA08E",
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
};

const emptyStateStyle = {
  textAlign: "center",
  padding: "64px 24px",
  background: "white",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
};

const emptyIconStyle = {
  fontSize: "64px",
  marginBottom: "16px",
};

const machinesGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
  gap: "10px",
};

const machineCardStyle = {
  background: "white",
  borderRadius: "16px",
  padding: "24px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  cursor: "grab",
  transition: "all 0.3s ease",
  border: "2px solid transparent",
};

const cardHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "20px",
  paddingBottom: "16px",
  borderBottom: "2px solid #f1f5f9",
};

const cardHeaderLeftStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
};

const dragHandleStyle = {
  fontSize: "20px",
  color: "#ffffff",
  cursor: "grab",
  userSelect: "none",
  lineHeight: "1",
  textShadow: "0 1px 2px rgba(0,0,0,0.55)",
};

const machineNameStyle = {
  margin: "0 0 6px 0",
  fontSize: "20px",
  fontWeight: "600",
  color: "#0f172a",
};

const orderBadgeStyle = {
  display: "inline-block",
  padding: "4px 12px",
  background: "#e0f2fe",
  color: "#0369a1",
  borderRadius: "6px",
  fontSize: "12px",
  fontWeight: "600",
};

const removeButtonStyle = {
  background: "transparent",
  color: "#f8fafc",
  border: "none",
  borderRadius: "8px",
  width: "28px",
  height: "28px",
  cursor: "pointer",
  fontSize: "18px",
  fontWeight: "600",
  transition: "all 0.2s",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const liveDataStyle = {
  background: "#ffffff",
  padding: "0",
  borderRadius: "12px",
  border: "1px solid #dbe4f0",
  overflow: "hidden",
  height: "252px",
  display: "flex",
  alignItems: "stretch",
};

const cardImageSectionStyle = {
  flex: "0 0 35%",
  minWidth: "90px",
  maxWidth: "120px",
  minHeight: "100%",
  borderRight: "1px solid #e2e8f0",
  position: "relative",
};

const virtualIconContainerStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  background: "linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.5) 100%)",
};

const virtualIconStyle = {
  width: "44px",
  height: "44px",
  filter: "drop-shadow(0 2px 4px rgba(15,23,42,0.15))",
};

const virtualIconLabelStyle = {
  fontSize: "10px",
  fontWeight: "700",
  color: "#0f766e",
  letterSpacing: "0.4px",
};

const cardContentSectionStyle = {
  flex: 1,
  padding: "10px",
  background: "#ffffff",
};

const liveDataHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "8px",
  paddingBottom: "8px",
  borderBottom: "1px solid #e2e8f0",
};

const liveDataHeaderTextStyle = {
  fontSize: "13px",
  fontWeight: "600",
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const liveDataCountStyle = {
  fontSize: "11px",
  color: "#64748b",
  background: "#e0f2fe",
  padding: "2px 8px",
  borderRadius: "4px",
  fontWeight: "600",
};

const liveDataGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "8px",
  alignContent: "start",
};

const liveDataItemStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  minHeight: "42px",
};

const liveDataLabelStyle = {
  fontSize: "10px",
  color: "#64748b",
  fontWeight: "600",
  textTransform: "uppercase",
  letterSpacing: "0.35px",
  textShadow: "none",
};

const liveDataValueStyle = {
  fontSize: "13px",
  color: "#0f172a",
  fontWeight: "700",
  textShadow: "none",
};

const parametersSectionStyle = {
  marginTop: "16px",
};

const parametersTitleStyle = {
  margin: "0 0 12px 0",
  fontSize: "14px",
  fontWeight: "600",
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const parametersGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
  gap: "8px",
};

const parameterChipStyle = {
  padding: "10px 14px",
  borderRadius: "10px",
  border: "2px solid",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "500",
  transition: "all 0.2s ease",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "8px",
};

const parameterChipActiveStyle = {
  background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
  borderColor: "#22c55e",
  color: "#166534",
};

const parameterChipInactiveStyle = {
  background: "#f8fafc",
  borderColor: "#e2e8f0",
  color: "#64748b",
};

const checkmarkStyle = {
  fontSize: "16px",
  fontWeight: "700",
};

const noParamsTextStyle = {
  marginTop: "12px",
  color: "#94a3b8",
  fontSize: "13px",
};

const toastStyle = (type) => ({
  position: "fixed",
  bottom: 24,
  right: 24,
  padding: "14px 24px",
  borderRadius: 12,
  background: type === "success" ? "#065f46" : "#991b1b",
  color: "#fff",
  fontSize: 14,
  fontWeight: 500,
  boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  animation: "slideIn 0.3s ease-out",
});

const compactSelectorStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  background: "white",
  padding: "12px 20px",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  background: 'white',
  padding: '24px',
  borderRadius: '16px',
  width: '90%',
  maxWidth: '500px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
};

const closeModalBtnStyle = {
  background: '#f1f5f9',
  border: 'none',
  borderRadius: '8px',
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '18px',
  cursor: 'pointer',
  color: '#64748b',
  transition: 'all 0.2s'
};

// Add CSS animation for spinner
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes slideIn {
    from { transform: translateY(100%); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;
document.head.appendChild(styleSheet);

export default CustomizationTab;
